import { useMemo, useState } from 'react'
import { Text, View } from 'react-native'
import { SessionDetails } from '@/src/dal/session-dal'
import { VoiceAssessmentBetaBadge } from '@/src/features/voice-assessment/components/VoiceAssessmentBetaBadge'
import { colors } from '@/src/theme'
import { body4, headline2, label1 } from '@/src/theme/typography'
import { useTranslation } from 'react-i18next'

import type { VoiceAssessmentScoreTimeframe } from '../../utils/voice-assessment-score-series'
import {
  buildVoiceAssessmentScoreSeries,
  getLatestScoredVoiceAssessmentSession,
} from '../../utils/voice-assessment-score-series'
import { VoiceAssessmentStatisticSegmentedControl } from './VoiceAssessmentStatisticSegmentedControl'
import { VoiceAssessmentStatisticsLineChart } from './VoiceAssessmentStatisticsLineChart'

interface VoiceAssessmentSingingScoreCardProps {
  sessions?: Pick<
    SessionDetails,
    | 'created'
    | 'score'
    | 'comfortableMax'
    | 'comfortableMin'
    | 'pitchMax'
    | 'pitchMin'
  >[]
}

const EMPTY_SCORE_SESSIONS: NonNullable<
  VoiceAssessmentSingingScoreCardProps['sessions']
> = []

export function VoiceAssessmentSingingScoreCard({
  sessions = EMPTY_SCORE_SESSIONS,
}: VoiceAssessmentSingingScoreCardProps) {
  const { t, i18n } = useTranslation(['statistics'])
  const [scoreTimeframe, setScoreTimeframe] =
    useState<VoiceAssessmentScoreTimeframe>('weekly')

  const latestScoredSession = useMemo(
    () => getLatestScoredVoiceAssessmentSession(sessions),
    [sessions],
  )

  const scoreSeries = useMemo(
    () =>
      buildVoiceAssessmentScoreSeries({
        sessions,
        timeframe: scoreTimeframe,
        locale: i18n.language,
      }),
    [i18n.language, scoreTimeframe, sessions],
  )

  const scoreOptions = useMemo(
    () =>
      [
        {
          key: 'weekly' as const,
          label: t('statistics:voice_assessment_segment_weekly'),
        },
        {
          key: 'monthly' as const,
          label: t('statistics:voice_assessment_segment_monthly'),
        },
        {
          key: 'sixMonths' as const,
          label: t('statistics:voice_assessment_segment_six_months'),
        },
      ] as const,
    [t],
  )

  return (
    <View className="gap-2 rounded-lg bg-background-secondary px-4 py-3">
      <View className="flex-row items-center justify-between">
        <View className="min-w-0 flex-1 flex-row items-center gap-2">
          <Text
            numberOfLines={1}
            style={[label1, { color: colors.textBody, flexShrink: 1 }]}
          >
            {t('statistics:voice_assessment_singing_score_label')}
          </Text>
          <VoiceAssessmentBetaBadge testID="singing-score-beta-badge" />
        </View>
        <View className="flex-row items-baseline gap-1.5">
          <Text style={[body4, { color: colors.textBody }]}>
            {t('statistics:voice_assessment_statistics_current_label')}
          </Text>
          <Text style={[headline2, { color: colors.textHeader }]}>
            {latestScoredSession == null
              ? t('statistics:voice_assessment_placeholder_dash')
              : latestScoredSession.score}
          </Text>
        </View>
      </View>
      <VoiceAssessmentStatisticSegmentedControl
        value={scoreTimeframe}
        onChange={setScoreTimeframe}
        options={[...scoreOptions]}
      />
      <VoiceAssessmentStatisticsLineChart
        points={scoreSeries.points}
        xLabels={scoreSeries.xLabels}
        visibleLabelCount={scoreSeries.visibleLabelCount}
        columnWidthMultiplier={scoreSeries.columnWidthMultiplier}
        locale={i18n.language}
      />
    </View>
  )
}
